/**
 * 舆论读数族（2026-10-23 从 MediaPanel / plotStage 收编成共享模块，茄子令「网友评价的点赞数
 * 用和数值事件一样的方法生成」）：评论数 / 转发数 / 楼层与帖子点赞的现场生成。
 *
 * **都是纯展示读数**：表里没有这些字段，生成不进任何结算、不落盘、不升存档版本。
 * 2026-10-17 茄子定「缺失的数据，你设定随机范围，然后随机生成」，并否掉了第一版
 * 「按玩家名气推无剧条目的量级」——八卦、盘点帖不带剧也能吵翻，量级是内容属性，
 * 所以锚改成表里那一格 `buzz`（话题度），缺省回退到事件所挂剧的热度（见 plotStage.mediaBeatOf）。
 *
 * 量级带宽按网上能查到的真实样本调过，不是拍脑袋：
 *  - 转发/讨论：一条原创微博 转发33,243／评论35,628＝0.93；另一条 590／1,749＝0.34
 *    所以取 25%~70%（第一版给的 6%~16% 被实测否了，差一个量级）
 *  - 楼层：热评一赞约为热评二的 8 倍多；另一帖热一1.9万／热二1.2万＝1.6
 *    所以首楼除以 3~8 得二楼，第三楼起每层 ×0.55~0.8
 *  - 反查判据：前十楼点赞拉得越平越像刷的（数据贴口径）所以这里刻意做陡
 * 样本都是网友手工整理、个位数条，所以带宽给得宽，具体数按内容做种在带内抖。
 */
import { GameState } from '../systems/GameState';
import { GameConfig } from '../core/GameConfig';

export const BUZZ = {
    /** 讨论 = 话题度 × 本带 */
    comments: [100, 220] as const,
    /** 转发 = 讨论 × 本带（%） */
    repostPct: [25, 70] as const,
    /** 首楼赞 = 讨论 × 本带（%） */
    topFloorPct: [20, 60] as const,
    /** 第二楼 = 首楼 ÷ 本带 */
    secondDiv: [3, 8] as const,
    /** 第三楼起 = 上一层 × 本带（%） */
    tailMult: [55, 80] as const,
    /** 微博帖子点赞（互动栏第三颗）= 讨论 × 本带（%）；微博点赞量级高于评论（沉默的赞），故上限高于 news */
    weiboLikePct: [90, 170] as const,
};

/** 表里没填 buzz、事件又没挂剧时的兜底档（30＝有点动静）。plotStage 的 warn 文案共用这一个数。 */
export const BUZZ_FALLBACK = 30;

/** 字符串做种取区间内整数（与 avatarColor 同一套 hash，纯展示、无随机源） */
export function seededRoll(seed: string, [min, max]: readonly [number, number]): number {
    let h = 0;
    for (const ch of seed) h = (h * 31 + ch.charCodeAt(0)) % 1000003;
    return min + (h % (max - min + 1));
}

/** 做种前缀＝内容 + 该事件落在的年月（渲染时现取单例，同 dateStr 口径）。
 *  月度链要重放，真随机会让已经看见过的数在刷新后凭空跳（2026-09-28 定的那条），故做种。 */
export function mediaSeed(content: string): string {
    const p = GameState.getInstance().player;
    return `${content}#${p.year + 1}-${p.month}`;
}

/** 这条帖子的讨论数（news 底部直接显示；hotpost 不显示，只当楼层点赞的锚） */
export function commentCount(buzz: number, seedText: string): number {
    return Math.max(1, Math.round(buzz * seededRoll(seedText, BUZZ.comments)));
}

/** news 底部第二颗：转发数（恒小于讨论，因为带是 25%~70%） */
export function repostCount(comments: number, seedText: string): number {
    return Math.max(1, Math.round(comments * seededRoll(`${seedText}|repost`, BUZZ.repostPct) / 100));
}

/** hotpost 第 floor 楼（0 起）的点赞数。逐层乘出来的，构造上必然降序，不靠排序。
 *  seedText＝这一帖的种子（全楼共用一枚），不是各楼自己的正文——链上每一枚倍数都从它派生。
 *  楼层极多时四舍五入可能压出相邻同值，那已是个位数赞的冷门帖，不再另设下限。 */
export function floorLikeCount(comments: number, seedText: string, floor: number): number {
    const top = Math.max(1, Math.round(comments * seededRoll(`${seedText}|top`, BUZZ.topFloorPct) / 100));
    if (floor === 0) return top;
    let v = top / seededRoll(`${seedText}|2`, BUZZ.secondDiv);
    for (let i = 2; i <= floor; i++) v = v * seededRoll(`${seedText}|${i}`, BUZZ.tailMult) / 100;
    return Math.max(1, Math.round(v));
}

/** 微博帖子的点赞数（互动栏第三颗）＝讨论 × 本带；比讨论高（微博点赞多为「沉默的赞」） */
export function weiboLikeCount(comments: number, seedText: string): number {
    return Math.max(1, Math.round(comments * seededRoll(`${seedText}|wlike`, BUZZ.weiboLikePct) / 100));
}

/**
 * **数值事件的话题度＝按名气换算**（2026-10-22 用户令「数值随机事件的话题度按照名气来换算，名气越高话题度越高」）。
 * `buzz = clamp(round(BASE × 名气^POWER), 1, MAX)`；系数在 `GameConfig.NUMERIC_EVENT_BUZZ_FROM_FAME`。
 * 幂函数是为了把四档铺开：名气 30→6／80→10／250→20／650→35／1300→53／2200→72／15000→255／30000→300（封顶 300＝顶流线，2026-10-31 重排随门槛顺移）
 */
export function buzzOfFame(fame: number): number {
    const { BASE, POWER, MAX } = GameConfig.NUMERIC_EVENT_BUZZ_FROM_FAME;
    return Math.max(1, Math.min(MAX, Math.round(BASE * Math.pow(Math.max(0, fame), POWER))));
}
